/**
 * סוג מתנדבים למשימה - פרטני, קבוצות, או גם וגם.
 * ערך חסר/לא תקין מטופל כ-INDIVIDUAL.
 */
export const OpportunityVolunteerMode = {
  INDIVIDUAL: "INDIVIDUAL",
  GROUP: "GROUP",
  BOTH: "BOTH",
} as const;

export type OpportunityVolunteerModeType =
  (typeof OpportunityVolunteerMode)[keyof typeof OpportunityVolunteerMode];

export const OPPORTUNITY_VOLUNTEER_MODE_HEB: Record<
  OpportunityVolunteerModeType,
  string
> = {
  INDIVIDUAL: "פרטני",
  GROUP: "קבוצות",
  BOTH: "גם וגם",
};

export const DEFAULT_OPPORTUNITY_VOLUNTEER_MODE =
  OpportunityVolunteerMode.INDIVIDUAL;

const VALID_MODES: Set<OpportunityVolunteerModeType> = new Set(
  Object.values(OpportunityVolunteerMode),
);

export function resolveOpportunityVolunteerMode(
  mode?: OpportunityVolunteerModeType | string | null,
): OpportunityVolunteerModeType {
  const value = mode as OpportunityVolunteerModeType;
  return value && VALID_MODES.has(value)
    ? value
    : DEFAULT_OPPORTUNITY_VOLUNTEER_MODE;
}

/** GROUP or BOTH - הרשמת קבוצה מותרת. */
export function allowsGroupVolunteer(
  mode?: OpportunityVolunteerModeType | string | null,
): boolean {
  const resolved = resolveOpportunityVolunteerMode(mode);
  return (
    resolved === OpportunityVolunteerMode.GROUP ||
    resolved === OpportunityVolunteerMode.BOTH
  );
}

/** INDIVIDUAL or BOTH - הרשמת מתנדב באפליקציה (לא GROUP בלבד). */
export function allowsIndividualVolunteerApp(
  mode?: OpportunityVolunteerModeType | string | null,
): boolean {
  const resolved = resolveOpportunityVolunteerMode(mode);
  return (
    resolved === OpportunityVolunteerMode.INDIVIDUAL ||
    resolved === OpportunityVolunteerMode.BOTH
  );
}

/** באדמין תמיד מותר לרשום מתנדב בודד. */
export function allowsIndividualVolunteerAdmin(
  _mode?: OpportunityVolunteerModeType | string | null,
): boolean {
  return true;
}

export function isGroupOnlyVolunteerMode(
  mode?: OpportunityVolunteerModeType | string | null,
): boolean {
  return (
    resolveOpportunityVolunteerMode(mode) === OpportunityVolunteerMode.GROUP
  );
}

export function volunteerModeFromLegacyIsGroup(
  isGroup: boolean,
): "GROUP" | "INDIVIDUAL" {
  return isGroup
    ? OpportunityVolunteerMode.GROUP
    : OpportunityVolunteerMode.INDIVIDUAL;
}

/** דגל ישן לאפליקציות שעדיין קוראות isGroup. true כשאפשר להירשם כקבוצה. */
export function legacyIsGroupFromVolunteerMode(
  mode?: OpportunityVolunteerModeType | string | null,
): boolean {
  return allowsGroupVolunteer(mode);
}

export function withLegacyIsGroup<T extends { volunteerMode?: string | null }>(
  opportunity: T,
): T & { isGroup: boolean } {
  return {
    ...opportunity,
    isGroup: legacyIsGroupFromVolunteerMode(opportunity.volunteerMode),
  };
}

/** volunteerMode מנצח; אחרת isGroup מהקליינט הישן; אחרת פרטני. */
export function resolveVolunteerModeFromClientInput(input: {
  volunteerMode?: string | null;
  isGroup?: boolean | null;
}): OpportunityVolunteerModeType {
  if (
    input.volunteerMode &&
    VALID_MODES.has(input.volunteerMode as OpportunityVolunteerModeType)
  ) {
    return input.volunteerMode as OpportunityVolunteerModeType;
  }
  if (typeof input.isGroup === "boolean") {
    return volunteerModeFromLegacyIsGroup(input.isGroup);
  }
  return DEFAULT_OPPORTUNITY_VOLUNTEER_MODE;
}

type OpportunityVolunteerModeFields = {
  volunteerMode?: string | null;
  isGroup?: boolean | null;
};

/** אפליקציה חדשה מול payload ישן: volunteerMode, ואם חסר אז isGroup. */
export function resolveVolunteerModeFromOpportunity(
  opportunity?: OpportunityVolunteerModeFields | null,
): OpportunityVolunteerModeType {
  return resolveVolunteerModeFromClientInput({
    volunteerMode: opportunity?.volunteerMode,
    isGroup: opportunity?.isGroup,
  });
}

export function opportunityAllowsGroupVolunteer(
  opportunity?: OpportunityVolunteerModeFields | null,
): boolean {
  return allowsGroupVolunteer(resolveVolunteerModeFromOpportunity(opportunity));
}

export function opportunityIsGroupOnly(
  opportunity?: OpportunityVolunteerModeFields | null,
): boolean {
  return isGroupOnlyVolunteerMode(
    resolveVolunteerModeFromOpportunity(opportunity),
  );
}
